import { batch } from "react-redux";
import type { AddToCartSource } from "~/app/analytics/types";
import { AnalyticsMQ } from "~/app/analytics/AnalyticsMQ/AnalyticsMQ";
import type { PurchasePlace, PurchaseType } from "~/app/analytics/ga/recommendations/types";
import { getStore } from "~/app/store/getStore";
import { queryClient } from "~/app/queryClient";
import { isSessionDuplicateInvariantError, pageDataKeys } from "~/app/api/pageData";
import { TranslationType } from "~/common/types/TranslationModel";
import { toMoxyDate, toMoxyDateNullable } from "~/common/types/MoxyDate";
import { openModal } from "~/common/utils/ModalService";
import { userIsSalesman } from "~/domains/user/utils/roles";
import CartSuccessModal from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal";
import { addQuantity } from "~/features/addToCart/redux/cartAmounts/actions";
import { sendAddToQuote, type AddToQuoteApiItem } from "~/features/cart/activeQuote/api/addToQuote";
import {
  sendAddToCart,
  type AddToCartApiItem,
  type AddToCartPartApiItem,
  type AddToCartResponseItem,
  type BulkAddToCartResponseDTO,
} from "~/features/cart/cart/api/AddToCart";
import { ensureActiveCartForContext } from "~/features/cart/cart/api/CartEnsure";
import { replaceCartWithEntries } from "~/features/cart/cart/api/ReplaceCartEntries";
import { clearCart } from "~/features/cart/cart/redux/actions/cartPageActions";
import { CLEAR_CART } from "~/features/cart/cart/redux/actions/actionTypes";
import { mapAddToCartResponseItemsToAnalyticsProducts } from "~/features/cart/cart/utils/addToCartResponseItems";
import { resolveActiveCartResourceId } from "~/features/cart/cart/utils/cartResourceIdentity";
import { replaceMinicartProjection } from "~/features/cart/minicart/minicartRedux";
import { addMessageAdvanced } from "~/features/toasts/redux/actions";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { isPhaseOut2Enabled } from "~/app/config/featureFlags";
import { useTranslation } from "react-i18next";
import { Snackbar } from "~/common/mui";
import { SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";

export interface AddToCartPartItem {
  productCode: string;
  quantity: number;
  packageCode: string;
  packageSize: number;
  primaryWarehouseCode: string;
  partTypeCode: string;
  manualUpdated: boolean;
  originCountry: string;
}

export interface AddToCartItem {
  productCode: string;
  packageSize?: number;
  packageCode?: string;
  quantity: number;
  createNewPackageEntry?: boolean;
  comment?: string;
  internalComment?: string;
  selectedDeliveryDate?: Date | null;
  selectedDeliveryDateIsManual?: boolean;
  primaryWarehouseCode?: string;
  contractNumber?: string;
  contractLine?: string;
  contractOpenQuantity?: number;
  priceReference?: string;
  position?: number;
  quoteLine?: string;
  quoteNumber?: string;
  quoteScheduleLineNum?: string;
  quoteMinQuantity?: number;
  isBundled?: boolean;
  quoteExpiryDate?: Date | null;
  logisticalComment?: string;
  estimatedShippingDate?: Date | null;
  restrictedPackage?: boolean;
  optionalParts?: AddToCartPartItem[];
  customerOrderNumber?: string;
  originCountry?: string;
  salesCampaignCode?: string;
  offerPrice?: number;
  targetPrice?: number;
  msp?: number;
  rsp?: number;
  recommendationType?: string;
  recommendationPlace?: string;
}

export type AddToCartSuccessUi = "modal" | "toast" | "none";
export type AddToCartErrorUi = "toast" | "none";

export interface AddToCartOptions {
  items: AddToCartItem[];
  page: PurchasePlace;
  source?: AddToCartSource;
  recommendationType?: PurchaseType;
  recommendationPlace?: PurchasePlace;
  successUi?: AddToCartSuccessUi;
  errorUi?: AddToCartErrorUi;
  overrideCart?: boolean;
  skipCartContentTracking?: boolean;
}

export async function addToCart({
  items,
  page,
  source = "unknown",
  recommendationType,
  recommendationPlace,
  successUi,
  errorUi = "toast",
  overrideCart = false,
  skipCartContentTracking = false,
}: AddToCartOptions): Promise<AddToCartResponseItem[]> {
  const dispatch = getStore().dispatch;
  const isSalesman = userIsSalesman();
  const effectiveSuccessUi = successUi ?? (isSalesman ? "toast" : "modal");

  try {
    if (!isSalesman && isSessionDuplicateInvariantError(queryClient.getQueryState(pageDataKeys.session())?.error)) {
      throw new Error("Cart mutations are disabled while Session Data reports duplicate carts");
    }

    let responseItems: AddToCartResponseItem[];
    let atomicReplacementSucceeded = false;
    if (isSalesman) {
      if (overrideCart) {
        await dispatch(clearCart(undefined));
      }
      responseItems = await addFlowItemsToQuote(items, {
        recommendationType,
        recommendationPlace,
      });
    } else {
      const cartApiItems = items.map(mapAddToCartItemToCartApiItem);
      if (overrideCart) {
        const activeCartId = await resolveActiveCartResourceId();
        if (!activeCartId) {
          throw new Error("Cannot replace cart without an explicit Active Cart identity");
        }
        responseItems = await replaceCartWithEntries(activeCartId, cartApiItems, skipCartContentTracking);
        replaceRetiredCartClientState(dispatch, responseItems);
        invalidateReplacementServerState();
        atomicReplacementSucceeded = true;
      } else {
        responseItems = await sendOrdinaryCartAdd(cartApiItems, skipCartContentTracking);
      }
    }

    if (!atomicReplacementSucceeded) {
      updateCartAmounts(responseItems);
    }

    if (!isSalesman) {
      trackAddToCartResponse(responseItems, { source, recommendationType, recommendationPlace });
    }

    showSuccessUi(effectiveSuccessUi, {
      page,
      responseItems,
      initialCount: totalQuantity(items),
      isSalesman,
    });

    return responseItems;
  } catch (error) {
    if (errorUi === "toast") {
      dispatch(
        addMessageAdvanced("error", {
          type: TranslationType.INTERNAL,
          value: isSalesman ? "addToQuote.failure" : "addToCart.failure",
          params: { count: totalQuantity(items) },
        }),
      );
    }
    throw error;
  }
}

async function sendOrdinaryCartAdd(
  cartApiItems: AddToCartApiItem[],
  skipCartContentTracking: boolean,
): Promise<AddToCartResponseItem[]> {
  const cart = await ensureActiveCartForContext();
  return sendAddToCart(cartApiItems, skipCartContentTracking, cart);
}

function replaceRetiredCartClientState(
  dispatch: ReturnType<typeof getStore>["dispatch"],
  responseItems: AddToCartResponseItem[],
) {
  batch(() => {
    dispatch({ type: CLEAR_CART });
    responseItems.forEach((item) => {
      dispatch(addQuantity(item.productCode, item.packCode, item.quantity, item.priceReference ?? ""));
    });
    dispatch(replaceMinicartProjection(totalQuantity(responseItems)));
  });
}

function invalidateReplacementServerState() {
  void Promise.all([
    queryClient.invalidateQueries({
      exact: true,
      queryKey: pageDataKeys.session(),
    }),
    queryClient.invalidateQueries({
      exact: true,
      queryKey: pageDataKeys.page("cart"),
    }),
  ]).catch(() => {
    // The atomic replacement already committed. Cache invalidation cannot make it retryable.
  });
}

export function mapAddToCartItemToCartApiItem(item: AddToCartItem): AddToCartApiItem {
  return {
    productCode: item.productCode,
    packageSize: item.packageSize,
    packageCode: item.packageCode ?? INVALID_PACKAGE_CODE,
    quantity: item.quantity,
    createNewPackageEntry: item.createNewPackageEntry,
    comment: item.comment,
    selectedDeliveryDate: toMoxyDateNullable(item.selectedDeliveryDate),
    selectedDeliveryDateIsManual: item.selectedDeliveryDateIsManual ?? !!item.selectedDeliveryDate,
    primaryWarehouseCode: item.primaryWarehouseCode ?? "",
    contractNumber: item.contractNumber,
    contractLine: item.contractLine,
    contractOpenQuantity: item.contractOpenQuantity,
    priceReference: item.priceReference ?? "",
    quoteLine: item.quoteLine,
    quoteNumber: item.quoteNumber,
    quoteScheduleLineNum: item.quoteScheduleLineNum,
    quoteMinQuantity: item.quoteMinQuantity,
    isBundled: item.isBundled,
    quoteExpiryDate: toMoxyDateNullable(item.quoteExpiryDate),
    logisticalComment: item.logisticalComment,
    estimatedShippingDate: toMoxyDateNullable(item.estimatedShippingDate),
    isRestrictedPackage: item.restrictedPackage ?? false,
    orderEntryNumber: item.position,
    optionalParts: item.optionalParts?.map(mapAddToCartPartItemToCartPartApiItem),
    customerOrderNumber: item.customerOrderNumber,
    originCountry: item.originCountry ?? "",
    salesCampaignCode: item.salesCampaignCode,
  };
}

export function mapAddToCartItemToQuoteApiItem(item: AddToCartItem): AddToQuoteApiItem {
  return {
    productCode: item.productCode,
    packageSize: item.packageSize,
    packageCode: item.packageCode ?? INVALID_PACKAGE_CODE,
    quantity: item.quantity,
    comment: item.comment,
    internalComment: item.internalComment ? item.internalComment + "\n" : "",
    selectedDeliveryDate: toMoxyDate(item.selectedDeliveryDate ?? new Date()),
    primaryWarehouseCode: item.primaryWarehouseCode ?? "",
    offerPrice: item.offerPrice,
    targetPrice: item.targetPrice,
    minimumSellingPrice: item.msp,
    recommendedSellingPrice: item.rsp,
    priceReference: item.priceReference ?? "",
  };
}

function mapAddToCartPartItemToCartPartApiItem(item: AddToCartPartItem): AddToCartPartApiItem {
  return {
    productCode: item.productCode,
    quantity: item.quantity,
    packageCode: item.packageCode,
    packageSize: item.packageSize,
    primaryWarehouseCode: item.primaryWarehouseCode,
    partTypeCode: item.partTypeCode,
    manualUpdated: item.manualUpdated,
    originCountry: item.originCountry,
  };
}

async function addFlowItemsToQuote(
  items: AddToCartItem[],
  context: { recommendationType?: PurchaseType; recommendationPlace?: PurchasePlace },
): Promise<AddToCartResponseItem[]> {
  const response = await sendAddToQuote(items.map(mapAddToCartItemToQuoteApiItem), context);
  return readQuoteResponseItems(response);
}

async function readQuoteResponseItems(response: Response): Promise<AddToCartResponseItem[]> {
  const body = (await response.json()) as Partial<BulkAddToCartResponseDTO>;
  if (!Array.isArray(body.items) || !body.items.every(isAddToCartResponseItem)) {
    throw new Error("Quote add-to-cart returned an invalid response");
  }
  return body.items;
}

function isAddToCartResponseItem(item: unknown): item is AddToCartResponseItem {
  return (
    typeof item === "object" &&
    item !== null &&
    typeof (item as AddToCartResponseItem).pk === "number" &&
    typeof (item as AddToCartResponseItem).productCode === "string" &&
    typeof (item as AddToCartResponseItem).packCode === "string" &&
    typeof (item as AddToCartResponseItem).quantity === "number"
  );
}

function updateCartAmounts(responseItems: AddToCartResponseItem[]) {
  const dispatch = getStore().dispatch;

  batch(() => {
    responseItems.forEach((item) => {
      dispatch(addQuantity(item.productCode, item.packCode, item.quantity, item.priceReference ?? ""));
    });
  });
}

function showSuccessUi(
  successUi: AddToCartSuccessUi,
  {
    page,
    responseItems,
    initialCount,
    isSalesman,
  }: { page: PurchasePlace; responseItems: AddToCartResponseItem[]; initialCount: number; isSalesman: boolean },
) {
  const dispatch = getStore().dispatch;

  if (successUi === "modal") {
    const resultCount = totalQuantity(responseItems);
    const quantityAdjusted = resultCount !== initialCount;

    // No better place to show it right now..
    if (quantityAdjusted && isPhaseOut2Enabled() && page === "WISHLIST") {
      openModal("qty-adjusted-snackbar", (close) => <QtyAdjustedSnackbar close={close} />);
    }

    openModal("add-to-cart-dialog", (close) => (
      <CartSuccessModal onClose={close} page={page} responseItems={responseItems} />
    ));
  }

  if (successUi === "toast") {
    dispatch(
      addMessageAdvanced("info", {
        type: TranslationType.INTERNAL,
        value: isSalesman ? "addToQuote.success" : "addToCart.success",
        params: { count: initialCount },
      }),
    );
  }
}

function trackAddToCartResponse(
  responseItems: AddToCartResponseItem[],
  context: {
    source: AddToCartSource;
    recommendationType?: PurchaseType;
    recommendationPlace?: PurchasePlace;
  },
) {
  if (!responseItems.length) {
    return;
  }

  const isRecommendation = !!context.recommendationType && !!context.recommendationPlace;

  AnalyticsMQ.instance.push("addToCart", {
    products: mapAddToCartResponseItemsToAnalyticsProducts(responseItems),
    context: {
      source: context.source,
      recommendationPlace: isRecommendation ? context.recommendationPlace : undefined,
      recommendationType: isRecommendation ? context.recommendationType : undefined,
    },
  });
}

function totalQuantity(items: { quantity: number }[]) {
  return items.reduce((sum, item) => sum + item.quantity, 0);
}

const QtyAdjustedSnackbar = ({ close }: { close: () => void }) => {
  const { t } = useTranslation();

  return (
    <Snackbar
      open={true}
      message={t("product.phaseout.qtyAdjusted.past", "Your quantity has been adjusted")}
      onClose={close}
      action={<SnackbarCloseButton onClick={close} />}
    />
  );
};
